import { Injectable } from '@jkhy/frontend-utils/di';
import {
    AddItemRequestModelDto,
    AuthenticateRequestModelDto,
    AuthenticatedResponseModelDto,
    CreateBatchRequestModelDto,
    GetCustomersRequestModelDto,
    GetItemImagesRequestModelDto,
    GetItemsRequestModelDto,
    ItemModelDto,
    RemoteDepositCaptureClient,
    UpdateBatchRequestModelDto,
} from '@treasury/api/channel';
import { HttpResponse } from '@treasury/api/channel/generated/http-client';
import { ConfigurationService } from '@treasury/core/config';
import { v4 as uuid } from 'uuid';
import { DeviceService } from '../services/device/device.service';
import { LogoutManager } from '../services/logout/logout-manager';
import { defaultItemModel } from './data/item.data';
import { RemoteDepositBatch } from './remote-deposit-batch.entity';
import { RemoteDepositCustomer } from './remote-deposit-customer.entity';
import { RemoteDepositDataField } from './remote-deposit-data-field';
import { RemoteDepositError } from './remote-deposit-error';
import { RemoteDepositImage } from './remote-deposit-image.entity';
import { RemoteDepositItem } from './remote-deposit-item.entity';
import { RemoteDepositLocation } from './remote-deposit-location.entity';

enum RdcResult {
    Error,
    Success,
    ValidationError,
    NoChange,
}

@Injectable()
export class RemoteDepositCaptureService {
    constructor(
        private readonly client: RemoteDepositCaptureClient,
        private readonly config: ConfigurationService,
        private readonly deviceService: DeviceService,
        private readonly logoutManager: LogoutManager
    ) {}

    public securityToken?: string;

    public isAuthenticated = false;

    public async getDataFields(locationReference: string) {
        if (!this.isAuthenticated) {
            await this.authenticateRemoteDepositCapture();
        }

        const settingsResponse = await this.client.remoteDepositCaptureGetSettings({
            securityToken: this.securityToken,
        });

        // If dataEntry is not enabled then don't attempt to get data fields so we do not throw an error
        if (!settingsResponse.data.showDataEntry) {
            return [];
        }

        const response = await this.client.remoteDepositCaptureGetDataFields({
            locationReference,
            securityToken: this.securityToken,
        });
        const data = this.validateResponse(response, 'dataFields', 'Failed to get data fields');
        return data?.map(field => new RemoteDepositDataField(field));
    }

    public async getLocations() {
        if (!this.isAuthenticated) {
            await this.authenticateRemoteDepositCapture();
        }
        const response = await this.client.remoteDepositCaptureGetLocations({
            securityToken: this.securityToken,
        });
        const data = this.validateResponse(response, 'locations', 'Failed to get locations');

        return data?.map(location => new RemoteDepositLocation(location)) ?? [];
    }

    public async getCustomers(
        accountNumber: string = '',
        customerName: string = '',
        customerNumber: string = ''
    ) {
        if (!this.isAuthenticated) {
            await this.authenticateRemoteDepositCapture();
        }
        const body: GetCustomersRequestModelDto = {
            accountNumber,
            customerName,
            customerNumber,
            securityToken: this.securityToken,
        };
        const response = await this.client.remoteDepositCaptureGetCustomers(body);
        const data = this.validateResponse(response, 'customers', 'Failed to get customers');
        return data?.map(customer => new RemoteDepositCustomer(customer));
    }

    public async openDeposit(location: RemoteDepositLocation, batchNumber: string = uuid()) {
        if (!this.isAuthenticated) {
            await this.authenticateRemoteDepositCapture();
        }
        if (!this.securityToken || !location.locationReference) {
            throw new RemoteDepositError(
                `Invalid data attempting to create a new deposit! ${!this.securityToken ? 'Token is empty. ' : ''}${!location.locationReference ? 'LocationReference is empty.' : ''}`
            );
        }
        const batchRequest: CreateBatchRequestModelDto = {
            securityToken: this.securityToken,
            locationReference: location.locationReference,
            batchNumber,
        };
        const response = await this.client.remoteDepositCaptureCreateBatch(batchRequest);
        const data = this.validateResponse(response, 'batch', 'Failed to create deposit');
        return new RemoteDepositBatch(data, location);
    }

    public async addItem(item: AddItemRequestModelDto, batchReference: string) {
        if (!this.isAuthenticated) {
            await this.authenticateRemoteDepositCapture();
        }
        // This is a workaround for the API not accepting the base64 string with the data:image/jpeg;base64, prefix
        const frontImage = item.frontImage?.split(',')[1];
        const rearImage = item.rearImage?.split(',')[1];
        const itemRequest: AddItemRequestModelDto = {
            ...item,
            frontImage,
            rearImage,
            batchReference,
            securityToken: this.securityToken,
        };
        const response = await this.client.remoteDepositCaptureAddItem(itemRequest);
        return this.validateResponse(response, 'item', 'Failed to add item');
    }

    public async deleteItem(itemReference: string) {
        type UpdateItemResponseModelDto = { item: ItemModelDto; result: number };
        if (!this.isAuthenticated) {
            await this.authenticateRemoteDepositCapture();
        }
        const response = (await this.client.remoteDepositCaptureDeleteItem({
            itemReference,
            securityToken: this.securityToken,
        })) as HttpResponse<UpdateItemResponseModelDto>;
        return this.validateResponse(response, 'item', 'Failed to remove item');
    }

    public async closeDeposit(batchReference: string) {
        const closeBatchRequest: UpdateBatchRequestModelDto = {
            batchReference,
            securityToken: this.securityToken,
        };
        const response = await this.client.remoteDepositCaptureCloseBatch(closeBatchRequest);
        return this.validateResponse(response, 'batch', 'Failed to close deposit');
    }

    public async deleteDeposit(batchReference: string) {
        const deleteBatchRequest: UpdateBatchRequestModelDto = {
            batchReference,
            securityToken: this.securityToken,
        };
        const response = await this.client.remoteDepositCaptureDeleteBatch(deleteBatchRequest);
        return this.validateResponse(response, 'batch', 'Failed to delete deposit');
    }

    public async getItems(depositId: string) {
        if (!this.isAuthenticated) {
            await this.authenticateRemoteDepositCapture();
        }

        const request: GetItemsRequestModelDto = {
            batchReference: depositId,
            securityToken: this.securityToken,
        };
        const response = await this.client.remoteDepositCaptureGetItems(request);
        const data = this.validateResponse(response, 'items', 'Failed to get deposit items');
        return data?.map(item => new RemoteDepositItem({ ...defaultItemModel, ...item }));
    }

    public async getItemImages(itemReference: string) {
        if (!this.isAuthenticated) {
            await this.authenticateRemoteDepositCapture();
        }

        const request: GetItemImagesRequestModelDto = {
            itemReference,
            securityToken: this.securityToken,
        };
        const response = await this.client.remoteDepositCaptureGetItemImages(request);
        const validatedResponse = this.validateResponse(
            response,
            'images',
            'Failed to get item images'
        );
        if (validatedResponse) {
            return new RemoteDepositImage(validatedResponse);
        }
        return new RemoteDepositImage({}) as RemoteDepositImage;
    }

    public async getDeposits() {
        if (!this.isAuthenticated) {
            await this.authenticateRemoteDepositCapture();
        }

        const response = await this.client.remoteDepositCaptureGetBatches({
            securityToken: this.securityToken,
        });
        const data = this.validateResponse(response, 'batches', 'Failed to get deposits');
        return data?.map(batch => new RemoteDepositBatch(batch));
    }

    public async hydrateDeposit(batchReference: string, deposit?: RemoteDepositBatch) {
        let loadedDeposit = deposit;
        if (!deposit) {
            loadedDeposit = await this.findDeposit(batchReference);
        }
        if (loadedDeposit && loadedDeposit.batchReference !== batchReference) {
            throw new RemoteDepositError(
                'Unable to open selected deposit: Invalid batch reference'
            );
        }
        if (loadedDeposit && loadedDeposit.batchReference === batchReference) {
            if (!loadedDeposit.location) {
                loadedDeposit.location = await this.findLocation(loadedDeposit.locationName);
            }
            if (!loadedDeposit.checks || loadedDeposit.checks.length === 0) {
                loadedDeposit.checks = await this.getItems(loadedDeposit.batchReference);
            }
            return loadedDeposit;
        }
        return undefined;
    }

    public async isUserConfiguredCorrectly() {
        const response = await this.client.remoteDepositCaptureGetStatus();
        return response.data.isUserConfigured && response.data.remoteDepositProductType === 2;
    }

    private async authenticateRemoteDepositCapture() {
        const rdcAuthRequest: AuthenticateRequestModelDto = {
            deviceTracking: {
                appBundleId: 'com.jackhenry.treasury.pwa',
                appVersion: this.config.version,
                deviceModel: this.deviceService.deviceModel,
                deviceSystemName: this.deviceService.osName,
                deviceSystemVersion: this.deviceService.osVersion,
            },
        };
        const response = await this.client.remoteDepositCaptureAuthenticate(rdcAuthRequest);
        if (response.data?.securityToken) {
            this.securityToken = response.data.securityToken;
            this.isAuthenticated = true;
            this.logoutManager.logout$.subscribe(() => {
                this.resetSession();
            });
            return true;
        }
        this.securityToken = undefined;
        this.isAuthenticated = false;
        throw new RemoteDepositError('Failed to authenticate with Remote Deposit Capture');
    }

    private resetSession() {
        this.isAuthenticated = false;
        this.securityToken = undefined;
    }

    private updateSecurityToken(token: string | undefined) {
        if (token) {
            this.securityToken = token;
        }
    }

    private validateResponse<T extends AuthenticatedResponseModelDto, K extends keyof T>(
        response: HttpResponse<T>,
        successField: K,
        errorMessage: string
    ) {
        this.updateSecurityToken(response.data?.securityToken);
        if (response.data?.result === RdcResult.Success) {
            return response.data[successField];
        }
        throw new RemoteDepositError(errorMessage, response.data ?? undefined);
    }

    private async findDeposit(batchReference: string) {
        const deposits = await this.getDeposits();
        return deposits?.find(currentDeposit => currentDeposit.batchReference === batchReference);
    }

    private async findLocation(locationName: string) {
        const locations = await this.getLocations();
        return locations?.find(location => location.name === locationName);
    }
}
